import React from 'react';
import { Helmet } from 'react-helmet';

const SEOHelmet = ({
  title = "Nikunj Navdiya - Full Stack Developer & Web3 Engineer",
  description = "Portfolio of Nikunj Navdiya - Software Developer, Web3 Engineer, and Community Builder. Specializing in React, Node.js, Blockchain development, and technical leadership.",
  keywords = "Nikunj Navdiya, Full Stack Developer, Web3 Developer, Software Engineer, React Developer, Node.js, Blockchain, Portfolio, India, Web3 Engineer, Community Builder, Technical Leader",
  canonical = null,
  image = "/avatar.jpg",
  type = "website",
  author = "Nikunj Navdiya",
  locale = "en_US",
  siteName = "Nikunj Navdiya Portfolio",
  structuredData = null,
  noIndex = false,
  world = "landing" // landing, community, web3, software
}) => {
  const baseUrl = "https://onchainik.me"; // Update with your actual domain
  const currentUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  // World-specific colors and themes
  const worldThemes = {
    landing: { color: "#3B82F6", theme: "default" },
    community: { color: "#10B981", theme: "success" },
    web3: { color: "#F59E0B", theme: "warning" },
    software: { color: "#6366F1", theme: "primary" }
  };

  const currentTheme = worldThemes[world] || worldThemes.landing;

  // Enhanced structured data for person/portfolio
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Nikunj Navdiya",
    "url": baseUrl,
    "image": imageUrl,
    "sameAs": [
      "https://twitter.com/nikunjnavdiya",
      "https://linkedin.com/in/nikunjnavdiya",
      "https://github.com/nikunjnavdiya",
      "https://farcaster.xyz/u/nikunjnavdiya"
    ],
    "jobTitle": "Full Stack Developer & Web3 Engineer",
    "worksFor": {
      "@type": "Organization",
      "name": "Freelance"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "alumniOf": "Engineering College",
    "knowsAbout": [
      "Software Development",
      "Web3 Development", 
      "Blockchain Technology",
      "Community Building",
      "React.js",
      "Node.js",
      "JavaScript",
      "TypeScript",
      "Solidity",
      "Ethereum",
      "DeFi",
      "NFT Development"
    ],
    "hasOccupation": {
      "@type": "Occupation",
      "name": "Full Stack Developer",
      "description": "Specializing in modern web technologies and blockchain development"
    },
    "description": description
  };

  // Additional structured data for website
  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": baseUrl,
    "description": description,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": author,
      "url": baseUrl
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={currentUrl} />}
      
      {/* Theme Color */}
      <meta name="theme-color" content={currentTheme.color} />
      <meta name="msapplication-TileColor" content={currentTheme.color} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${author} - Portfolio`} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@nikunjnavdiya" />
      <meta name="twitter:creator" content="@nikunjnavdiya" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={`${author} - Portfolio`} />
      
      {/* Additional Meta Tags for better SEO */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Nikunj Navdiya" />
      
      {/* Language and region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.country" content="India" />
      
      {/* Content type and encoding */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Security headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      
      {/* Rich Snippets / Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(websiteStructuredData)}
      </script>
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      
      {/* Favicons and App Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/avatar.jpg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      
      {/* Additional icon sizes for better device support */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      
      {/* Manifest for PWA */}
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

export default SEOHelmet; 